import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import type { LeadsTableUpsertRow } from "../utils/leadsImportToLeadsTable";

/** Row from MySQL `Leads` (GET /api/leads/table). */
export type LeadTableRow = {
  leadId: string;
  district: string | null;
  mandal: string | null;
  village: string | null;
  applicantName: string | null;
  mobileNumber: string | null;
  aadhaarNumber: string | null;
  /** Registration / sheet status from import. */
  status: string | null;
  /** HO triage from `Leads.ho_status` (approved | reject | on_hold; legacy approve read as approved). */
  hoStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

function normalizeHoStatusFromApi(hoStatus: string | null | undefined): string | null {
  if (hoStatus == null || !String(hoStatus).trim()) return null;
  const s = String(hoStatus).trim().toLowerCase();
  if (s === "approve") return "approved";
  return String(hoStatus).trim();
}

/** Load all rows from MySQL `Leads`. */
export async function fetchLeadsTable(): Promise<LeadTableRow[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/leads/table`);
  if (!res.ok) throw new Error(await parseError(res));
  const raw = (await res.json()) as LeadTableRow[];
  return Array.isArray(raw)
    ? raw.map((r) => ({
        ...r,
        hoStatus: normalizeHoStatusFromApi(r.hoStatus),
      }))
    : [];
}

/** Upsert rows into MySQL `Leads` (same DB as geo/applications when MYSQL_HOST is set). */
export async function postLeadsBulk(rows: LeadsTableUpsertRow[]): Promise<{ count: number }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/leads/table-bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leads: rows }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ count: number }>;
}

/** Values persisted to `Leads.ho_status`. Empty string clears HO status. Server accepts legacy "approve" as "approved". */
export type LeadHoAction = "approved" | "reject" | "on_hold" | "";

/** Update `Leads.ho_status` for one lead (`Status` from import is unchanged). */
export async function updateLeadHoStatus(leadId: string, hoStatus: LeadHoAction): Promise<LeadTableRow> {
  const id = encodeURIComponent(String(leadId ?? "").trim());
  const res = await apiFetch(`${getApiBaseUrl()}/api/leads/table/${id}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    /** `status` mirrors `hoStatus` so older API builds that only read `status` still receive the HO value. */
    body: JSON.stringify({ hoStatus, status: hoStatus }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<LeadTableRow>;
}
